import { useState } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  DocumentTextIcon,
  BellIcon,
} from '@heroicons/react/24/outline';

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  timezone: string;
  responseFormat: {
    markdown: boolean;
    codeBlocks: boolean;
    streamResponse: boolean;
    imageFormat: 'png' | 'jpg' | 'webp';
  };
  notifications: {
    modelErrors: boolean;
    tokenThresholds: boolean;
    completionAlerts: boolean;
    costAlerts: boolean;
    imageGeneration: boolean;
  };
}

const mockProfile: UserProfile = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  phone: '+1 (555) 123-4567',
  timezone: 'America/New_York',
  responseFormat: {
    markdown: true,
    codeBlocks: true,
    streamResponse: true,
    imageFormat: 'png',
  },
  notifications: {
    modelErrors: true,
    tokenThresholds: true,
    completionAlerts: false,
    costAlerts: true,
    imageGeneration: true,
  },
};

export default function Profile() {
  const [profile, setProfile] = useState<UserProfile>(mockProfile);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    // TODO: Implement API call to save profile
    setIsEditing(false);
  };

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserIcon className="h-6 w-6 text-white/90" />
            <h1 className="text-xl font-medium text-white">Profile Settings</h1>
          </div>
          <button
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
            className="btn-primary"
          >
            {isEditing ? 'Save Changes' : 'Edit Profile'}
          </button>
        </div>

        {/* Profile Form */}
        <div className="p-8 space-y-8">
          {/* Avatar Section */}
          <div className="flex items-center space-x-4">
            <div className="h-20 w-20 bg-white/[0.05] rounded-full flex items-center justify-center">
              {profile.avatar ? (
                <img
                  src={profile.avatar}
                  alt={profile.name}
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="h-10 w-10 text-white/50" />
              )}
            </div>
            {isEditing && (
              <button className="btn-secondary">
                Change Avatar
              </button>
            )}
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-white">Basic Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  disabled={!isEditing}
                  className="input w-full rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-white/30" />
                  </div>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    disabled={!isEditing}
                    className="input w-full pl-10 rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-white/30" />
                  </div>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    disabled={!isEditing}
                    className="input w-full pl-10 rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Timezone
                </label>
                <select
                  value={profile.timezone}
                  onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                  disabled={!isEditing}
                  className="input w-full rounded"
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Response Format */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <DocumentTextIcon className="h-6 w-6 text-white/90" />
              <h2 className="text-lg font-medium text-white">Response Format</h2>
            </div>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.responseFormat.markdown}
                  onChange={(e) => setProfile({
                    ...profile,
                    responseFormat: {
                      ...profile.responseFormat,
                      markdown: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Enable Markdown Formatting</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.responseFormat.codeBlocks}
                  onChange={(e) => setProfile({
                    ...profile,
                    responseFormat: {
                      ...profile.responseFormat,
                      codeBlocks: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Syntax Highlighting for Code Blocks</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.responseFormat.streamResponse}
                  onChange={(e) => setProfile({
                    ...profile,
                    responseFormat: {
                      ...profile.responseFormat,
                      streamResponse: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Stream Response in Real-time</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Image Format
                </label>
                <select
                  value={profile.responseFormat.imageFormat}
                  onChange={(e) => setProfile({
                    ...profile,
                    responseFormat: {
                      ...profile.responseFormat,
                      imageFormat: e.target.value as 'png' | 'jpg' | 'webp',
                    },
                  })}
                  disabled={!isEditing}
                  className="input w-full rounded"
                >
                  <option value="png">PNG - Lossless quality</option>
                  <option value="jpg">JPG - Smaller file size</option>
                  <option value="webp">WebP - Modern format</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <BellIcon className="h-6 w-6 text-white/90" />
              <h2 className="text-lg font-medium text-white">Notification Preferences</h2>
            </div>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.notifications.modelErrors}
                  onChange={(e) => setProfile({
                    ...profile,
                    notifications: {
                      ...profile.notifications,
                      modelErrors: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Model Errors and Warnings</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.notifications.tokenThresholds}
                  onChange={(e) => setProfile({
                    ...profile,
                    notifications: {
                      ...profile.notifications,
                      tokenThresholds: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Token Usage Thresholds</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.notifications.completionAlerts}
                  onChange={(e) => setProfile({
                    ...profile,
                    notifications: {
                      ...profile.notifications,
                      completionAlerts: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Completion Alerts</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.notifications.costAlerts}
                  onChange={(e) => setProfile({
                    ...profile,
                    notifications: {
                      ...profile.notifications,
                      costAlerts: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Cost Threshold Alerts</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={profile.notifications.imageGeneration}
                  onChange={(e) => setProfile({
                    ...profile,
                    notifications: {
                      ...profile.notifications,
                      imageGeneration: e.target.checked,
                    },
                  })}
                  disabled={!isEditing}
                  className="rounded border-white/20 bg-white/5 text-magick-purple focus:ring-magick-purple"
                />
                <span className="text-white/70">Image Generation Notifications</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
